import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import { Box, IconButton, InputBase } from "@mui/material";
import { Controller, useFormContext } from "react-hook-form";
import { IChecklistQuestionType } from "../../interfaces/CheckLists";
import { useLocale } from "../../providers/LocaleProvider";
import DeleteIcon from "../../resources/images/icons-delete-blue.svg";
import { Paper } from "../Paper";
import QuestionEditor from "./QuestionEditor";
import QuestionTypeSelector from "./QuestionTypeSelector";

interface QuestionItemProps {
  qIndex: number;
  onRemoveQuestion: () => void;
  canRemove: boolean;
  listQuestionTypes?: IChecklistQuestionType[];
  onChangeType: (questionIndex: number, prevTypeId: string, nextTypeId: string) => void;
  name: string;
  question: any;
  triggerValidationValueDebounced?: () => void;
  sectionIndex?: number;
}

export default function QuestionItem({
  qIndex,
  onRemoveQuestion,
  canRemove,
  listQuestionTypes,
  onChangeType,
  name,
  question,
  triggerValidationValueDebounced
}: QuestionItemProps) {
  const { translate } = useLocale();
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: question.id
  });

  const { control } = useFormContext();

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1
  };

  return (
    <div ref={setNodeRef} style={style}>
      <Paper
        titleSize={1}
        titleFontSize='16px'
        titleFontWeight={600}
        sx={{
          mb: 3,
          "&:hover .drag-handle": { opacity: 1 }
        }}
      >
        <Box>
          <Box
            sx={{
              display: "flex",
              alignItems: "flex-start",
              justifyContent: "space-between",
              width: "100%"
            }}
          >
            <Box sx={{ display: "flex", alignItems: "flex-start", width: "100%" }}>
              <IconButton
                className='drag-handle'
                {...attributes}
                {...listeners}
                sx={{
                  transition: "opacity 0.2s ease-in-out",
                  "&:hover": { opacity: 1, backgroundColor: "#DBEAFE" },
                  borderRadius: "5px",
                  cursor: "grab",
                  "&:active": { cursor: "grabbing" }
                }}
              >
                <DragIndicatorIcon fontSize='small' />
              </IconButton>

              <Controller
                control={control}
                name={`${name}.questionText`}
                render={({ field, fieldState }) => (
                  <InputBase
                    multiline
                    inputRef={field.ref}
                    className='klayo-textfield'
                    value={field.value}
                    fullWidth={false}
                    placeholder={translate("settingsViews.checklists.questionNamePlaceholder")}
                    onChange={(e) => {
                      field.onChange(e);
                      triggerValidationValueDebounced?.();
                    }}
                    sx={{
                      width: "100%",
                      "& input": {
                        fontSize: "16px",
                        fontWeight: 600,
                        flex: 1
                      },
                      borderRadius: "8px",
                      border: fieldState.error
                        ? "1px solid var(--torch-red-tr-500)"
                        : "1px solid transparent",
                      padding: "6px 10px 6px 6px ",
                      marginRight: 2
                    }}
                  />
                )}
              />
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <QuestionTypeSelector
                qIndex={qIndex}
                onChangeType={onChangeType}
                listQuestionTypes={listQuestionTypes}
                name={`${name}`}
              />
              {canRemove && (
                <IconButton onClick={onRemoveQuestion}>
                  <DeleteIcon />
                </IconButton>
              )}
            </Box>
          </Box>
          <QuestionEditor
            listQuestionTypes={listQuestionTypes}
            name={`${name}`}
            triggerValidationValueDebounced={triggerValidationValueDebounced}
          />
        </Box>
      </Paper>
    </div>
  );
}
